import { installSdk } from '@/sdk';
import { ApiError, getUpgradeStatus, isUpgradeTerminal, completeCheckout, waitForBackendCompletion, exchangeMagicToken, getSiteConfig, establishSession, sendScriptHeartbeat } from '@/api/client';
import { captureCampaignPromo, clearStashedCampaignPromo } from '@/campaignPromo';
import { ensureEzAuth, ensureEzAuthFacade } from '@/ensureEzAuth';
import { emit as emitAccessChange } from '@/accessEvents';
import { initAdRemoval, reconcileAdRemoval } from '@/adRemoval';
import { getPendingUpgrade, clearPendingUpgrade, hasSession, sessionVerified, getToken, softReverifyTried, markSoftReverifyTried, getPendingCheckout, clearPendingCheckout } from '@/session';
import { activateSandbox, beginSandboxMagicExchange, finishSandboxMagicExchange } from '@/sandboxMode';
import { logError } from '@/sdkConsole';
import type { CheckoutCompletionResponse } from '@/types';

const MAGIC_TOKEN_KEY = 'ezsm_verify';

// The widget is publisher-driven: nothing renders on load. We only restore any
// pending session state (magic-link return, interrupted checkout, ezAuth login)
// so it is in place before the publisher calls showPaywall / openDonation, then
// publish the API on window.ezsubscriptions.
//
// A classic <script> tag executes once per tag, so a page that includes the
// loader twice (publisher's own copy plus an injected one, or a duplicated
// embed) would otherwise bootstrap the SDK twice: two shadow roots and two sets
// of delegated document click listeners, making every surface — paywall,
// donation dialog, login — open twice on a single click. The first bootstrap
// publishes the ready API; a duplicate execution sees it and does nothing.
if (!sdkAlreadyInstalled()) {
  preconnectStripe();
  // Capture a campaign ?promo= before the API publishes, so no
  // showPaywall/openCheckout call can precede the stash.
  captureCampaignPromo();
  const restored = restoreSession();
  installSdk(restored.then(() => undefined), restored);
}

// preconnectStripe warms DNS+TLS to the Stripe origins checkout needs, at
// bootstrap rather than at checkout open: when prewarmStripe (sdk.ts) later
// starts the Stripe.js download, the connection is already up. No download
// happens here, so this is cheap on every pageview. api.stripe.com gets
// crossorigin because its requests are CORS fetches; the script/iframe/beacon
// origins must not set it — a crossorigin preconnect pool is not reused for
// non-CORS requests.
function preconnectStripe(): void {
  const head = document.head ?? document.documentElement;
  if (!head) {
    return;
  }
  const origins: Array<[href: string, cors: boolean]> = [
    ['https://js.stripe.com', false],
    ['https://m.stripe.com', false],
    ['https://q.stripe.com', false],
    ['https://r.stripe.com', false],
    ['https://api.stripe.com', true],
  ];
  for (const [href, cors] of origins) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (cors) {
      link.crossOrigin = 'anonymous';
    }
    head.appendChild(link);
  }
}

// sdkAlreadyInstalled reports whether a prior execution already published the
// ready API. Before load window.ezsubscriptions is the publisher's cmd-only stub
// (no `ready`); after installSdk it is the full API with ready === true.
function sdkAlreadyInstalled(): boolean {
  const existing = window.ezsubscriptions;
  return !!existing && 'ready' in existing && existing.ready;
}

// restoreSession performs the invisible, side-effecting work that must happen on
// every page load regardless of whether a paywall is shown: completing a
// magic-link login, resuming a checkout that confirmed payment but had not
// finished provisioning, and restoring an ezAuth login server-side. It never
// rejects so the API is always usable. Resolves to a checkout that completed
// on this load (a payment redirect or reload returning), for its success screen.
async function restoreSession(): Promise<CheckoutCompletionResponse | null> {
  // Clear the ad-removal reload guard and restore any stashed scroll position
  // after a reload disableAds triggered on the previous page load.
  initAdRemoval();
  // Sandbox entry precedes every other API call: the handoff exchange (or a
  // resumed sandbox session) re-points the API base at the sandbox host, and
  // every later fetch must already see it.
  await activateSandbox();
  // At most one heartbeat per hour per browser and none from named crawlers
  // outside sandbox (both gated inside), only reached via the dedup guard above so a duplicated
  // embed doesn't double-report.
  // Fires after sandbox activation so a sandbox page load reports to the
  // sandbox host (gated — the session established above rides along).
  sendScriptHeartbeat();
  await consumeMagicLinkFragment();
  const returned = await resumePendingCheckout();
  await restoreEzAuthSession();
  // Fire-and-forget: an applied upgrade announces itself via access:change, so
  // its status read (up to its timeout) must not delay SDK readiness.
  void resumePendingUpgrade();
  // Revalidate an active ad-free cookie against the live entitlement (fire-and-
  // forget). Runs after the session is restored above so an entitled visitor's
  // bearer is in place before the mint — otherwise the recheck would see no
  // session (fail-open anyway, but this avoids needless churn). Throttled inside.
  void reconcileAdRemoval();
  // Fire-and-forget: the watcher only registers a future onChange listener, so it
  // must not gate bootstrap (awaiting it would make the first hasAccess wait on
  // the ezAuth facade — and pay its init timeout a second time when ezAuth is down).
  void watchEzAuthLogin();
  return returned;
}

async function resumePendingUpgrade(): Promise<void> {
  const upgradeId = getPendingUpgrade();
  if (!upgradeId || !hasSession()) {
    return;
  }
  try {
    const result = await getUpgradeStatus(upgradeId);
    if (isUpgradeTerminal(result.status)) {
      clearPendingUpgrade();
    }
    if (result.status === 'applied') {
      emitAccessChange();
    }
  } catch (err) {
    // Expired session, another visitor's upgrade, or gone: this session can
    // never resolve the marker; after a re-login access is re-read anyway.
    if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) {
      clearPendingUpgrade();
    }
    logError('upgrade resume failed', err);
  }
}

// consumeMagicLinkFragment exchanges a single-use magic-link token delivered in
// the URL fragment (e.g. #ezsm_verify=<token>) for a session, then strips it
// from the URL so it does not linger in history or get re-processed.
async function consumeMagicLinkFragment(): Promise<void> {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return;
  const params = new URLSearchParams(hash);
  const token = params.get(MAGIC_TOKEN_KEY);
  if (!token) return;

  // A sandbox-minted token (sb marker) exchanges against the sandbox host —
  // the cross-browser case, where a content-unlock email from a sandbox
  // purchase is opened without an active sandbox session.
  const sandboxExchange = beginSandboxMagicExchange(token);
  try {
    await exchangeMagicToken(token);
    finishSandboxMagicExchange(sandboxExchange, true);
  } catch {
    // Invalid/expired link; fall through to normal gating (paywall/login).
    finishSandboxMagicExchange(sandboxExchange, false);
  } finally {
    params.delete(MAGIC_TOKEN_KEY);
    const remaining = params.toString();
    const url = window.location.pathname + window.location.search + (remaining ? `#${remaining}` : '');
    window.history.replaceState(null, '', url);
  }
}

// resumePendingCheckout finishes a checkout that confirmed payment but had not
// completed backend provisioning (e.g. the page reloaded or a 3DS redirect
// returned). completeCheckout stores the issued session if one is returned.
// Resolves to the result only when it completed, so the buyer sees the success
// screen; anything else stays silent (the webhook still provisions).
async function resumePendingCheckout(): Promise<CheckoutCompletionResponse | null> {
  const checkoutId = getPendingCheckout();
  if (!checkoutId) {
    return null;
  }
  // An existing session says nothing about this purchase (it can predate it),
  // so a cs_ always gets one completion attempt, as the in-page path does. A
  // sub_ id is a direct-created subscription (trial confirm / convert-to-paid)
  // completing after its first invoice's 3DS; it may not have flipped to
  // active yet on redirect return, so it gets the completion poll.
  if (checkoutId.startsWith('sub_')) {
    try {
      const completion = await waitForBackendCompletion(checkoutId);
      // A completed checkout consumes the campaign stash — this bootstrap
      // resume path never mounts a CheckoutFlow, so finishSuccess never runs.
      clearStashedCampaignPromo();
      return completion;
    } catch (err) {
      // intentionally tolerated: best-effort; the webhook reconcile provisions
      // independently.
      logError('checkout resume failed', err);
      return null;
    } finally {
      clearPendingCheckout();
    }
  }
  try {
    const completion = await completeCheckout(checkoutId);
    clearStashedCampaignPromo();
    return completion.status === 'completed' ? completion : null;
  } catch (err) {
    // intentionally tolerated: best-effort single attempt; the webhook
    // provisions independently.
    logError('checkout resume failed', err);
    return null;
  } finally {
    // Clear after one attempt to avoid repeated calls on every page load.
    clearPendingCheckout();
  }
}

// restoreEzAuthSession establishes a content session for a logged-in ezAuth
// visitor before bootstrap finishes, so the first hasAccess() sees the restored
// session instead of short-circuiting to login_required. It only runs for
// auth_mode=ezauth domains and only when no verified session was restored above
// (magic-link / pending checkout): a soft one (bare email, guest checkout) is
// replaced only by a verified ezAuth session, so a reader with a verified email
// can spend without the emailed-link step; a soft result keeps the existing
// session and is remembered for that token. ensureEzAuth reads window.ezAuth's access
// token; SM resolves it to an email server-side via PUA, so the plaintext email
// never reaches the page. Anonymous visitors and setup failures resolve to no
// token and leave the visitor as a guest.
async function restoreEzAuthSession(): Promise<void> {
  if (sessionVerified()) {
    return;
  }
  const softToken = getToken();
  if (softToken !== null && softReverifyTried(softToken)) {
    return;
  }
  const config = await getSiteConfig();
  if (config?.authMode !== 'ezauth') return;
  const token = await ensureEzAuth(config.domainId);
  if (!token) return;
  try {
    const replaced = await establishSession({ ezAuthToken: token }, undefined, undefined, { requireVerified: true });
    if (!replaced && softToken !== null && getToken() === softToken) {
      markSoftReverifyTried(softToken);
    }
  } catch (err) {
    logError('ezAuth session establish failed', err);
  }
}

// watchEzAuthLogin keeps content access live for an in-page ezAuth login that
// does not reload the page: restoreEzAuthSession() covers the load-time case,
// this covers a visitor who logs in through the publisher's in-page ezAuth UI
// after the page is already up. On a transition to 'authenticated' with no
// verified SM session yet it establishes the content session from the ezAuth
// token and fires access:change, so the publisher's access:change handler
// re-renders gated content without a reload.
//
// It only reacts to login, never to logout/anonymous: on an ezauth domain the SM
// session may have come from a magic link or guest checkout rather than ezAuth,
// and SM does not track that provenance — clearing here could drop a session that
// is not ezAuth's. An explicit sign-out is handled by ezsubscriptions.logout()
// (which ends the ezAuth session and clears the SM session together); a passive
// ezAuth logout without a reload is left to the next page load.
async function watchEzAuthLogin(): Promise<void> {
  // A visitor already verified at load has nothing to unlock, so do not set up
  // the watcher (and do no extra work) in that case. restoreEzAuthSession runs
  // first, so this also covers the logged-in-at-load visitor whose session it just
  // established.
  if (sessionVerified()) {
    return;
  }
  const config = await getSiteConfig();
  if (config?.authMode !== 'ezauth') return;
  const facade = await ensureEzAuthFacade(config.domainId);
  if (!facade) return;

  let sawInitialState = false;
  let lastStatus = '';
  facade.onChange((state) => {
    const previousStatus = lastStatus;
    lastStatus = state.status;
    // The first callback is the immediate current-state fire; the load-time state
    // is already handled by restoreEzAuthSession, so only act on a later
    // transition (an actual in-page login).
    if (!sawInitialState) {
      sawInitialState = true;
      return;
    }
    if (state.status !== 'authenticated' || sessionVerified()) {
      return;
    }
    // A soft session from an unverified ezAuth profile stays soft, so repeat
    // 'authenticated' fires must not re-establish (and emit) every time.
    if (hasSession() && previousStatus === 'authenticated') {
      return;
    }
    void establishEzAuthSessionLive(config.domainId);
  });
}

// establishEzAuthSessionLive reads the just-logged-in visitor's ezAuth token,
// exchanges it for an SM content session, and fires access:change on success so
// gated content unlocks in place. Never throws; a failed establish leaves the
// visitor a guest, same as the load-time path.
async function establishEzAuthSessionLive(domainId: number): Promise<void> {
  const token = await ensureEzAuth(domainId);
  if (!token) return;
  try {
    const authenticated = await establishSession({ ezAuthToken: token }, undefined, undefined, { requireVerified: true });
    if (authenticated) {
      emitAccessChange();
    }
  } catch (err) {
    logError('ezAuth live session establish failed', err);
  }
}
